import { useRef } from 'react'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { stringToDateWithTimezone } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { usePreview } from 'modules/shift_management/hooks/usePreview'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type DefaultShiftWithParamsForm from 'modules/shift_management/lib/forms/DefaultShiftWithParamsForm'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import type ShiftForm from 'modules/shift_management/lib/forms/ShiftForm'
import ShiftsEditForm from 'modules/shift_management/lib/forms/ShiftsEditForm'
import { TABS, type TabsType } from 'modules/shift_management/lib/types'
import Shift, { CREATION_MODE } from 'modules/shift_management/store/internal/models/Shift'
import PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

export interface Props {
  daysByRow: Record<string, string[]>
  customShiftForm?: ShiftForm | ShiftsEditForm | null
  defaultShiftForms: DefaultShiftWithParamsForm[]
  plannedBreakForms?: PlannedBreakForm[]
  selectedTab: TabsType
  shiftIdsToHide?: number[]
  isBackup?: boolean
}

const useAddShiftPreview = ({
  daysByRow,
  customShiftForm = null,
  defaultShiftForms,
  plannedBreakForms = [],
  selectedTab,
  shiftIdsToHide = [],
  isBackup,
}: Props) => {
  const [{ view }] = useSchedule()
  const [, dispatch] = usePreview()
  const isCustomShift = selectedTab === TABS.CUSTOM_SHIFT
  const resetShiftsTimeout = useRef<ReturnType<typeof setTimeout>>()
  const resetPlannedBreaksTimeout = useRef<ReturnType<typeof setTimeout>>()
  const daysByRowStr = JSON.stringify(Object.values(daysByRow))
  const customShiftFormStr = JSON.stringify(customShiftForm?.values)
  const defaultShiftFormsStr = JSON.stringify(defaultShiftForms)
  const plannedBreakFormsStr = JSON.stringify(plannedBreakForms)
  const shiftIdsToHideStr = JSON.stringify(shiftIdsToHide)
  const selectedDefaultShifts = useUnsafeMemo(
    () => defaultShiftForms.filter((form) => form.isSelected),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultShiftForms, defaultShiftFormsStr]
  )

  const formatDate = (date: string) => {
    const dateObj = stringToDateWithTimezone(date, 'ui_time')
    return dateObj.isValid() ? dateObj : stringToDateWithTimezone('00:00', 'ui_time')
  }

  const buildCustomShift = useUnsafeCallback(() => {
    if (!customShiftForm) return []
    const form = customShiftForm
    const isEdit = form instanceof ShiftsEditForm
    const startAt = isEdit ? form.get('startAt').value : form.get('start_time').value
    const endAt = isEdit ? form.get('endAt').value : form.get('end_time').value
    const extraHours = isEdit ? form.get('extraHours').value : form.get('extra_hours').value
    const locationId = isEdit ? +form.get('locationId').value : +form.get('location_id').value
    const workAreaId = isEdit ? +form.get('workAreaId').value : +form.get('work_area_id').value

    return [
      new Shift({
        id: 0,
        employeeId: 0,
        state: isBackup ? 'backup' : 'draft',
        name:
          customShiftForm.get('name').value || i18n.t('shift_management.preview_cell.shift_name'),
        startAt: formatDate(startAt),
        endAt: formatDate(endAt),
        defaultShiftId: null,
        defaultShiftTitle: null,
        extraHours,
        notes: customShiftForm.get('notes').value,
        locationId,
        workAreaId,
        preview: true,
        creationMode: CREATION_MODE.manual,
      }),
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customShiftFormStr])

  const buildSavedShifts = useUnsafeCallback(() => {
    return selectedDefaultShifts.flatMap((form: DefaultShiftWithParamsForm) => {
      return new Shift({
        id: 0,
        employeeId: 0,
        state: isBackup ? 'backup' : 'draft',
        name: form.get('title').value,
        startAt: formatDate(form.get('start_at').value),
        endAt: formatDate(form.get('end_at').value),
        defaultShiftId: form.get('id').value,
        extraHours: form.get('extra_hours').value,
        notes: form.get('notes').value,
        locationId: form.get('location_id').value,
        workAreaId: form.get('work_area_id').value,
        color: form.get('color').value,
        preview: true,
        creationMode: CREATION_MODE.manual,
      })
    })
  }, [selectedDefaultShifts])

  const buildPlannedBreaks = useUnsafeCallback(() => {
    return plannedBreakForms.flatMap((form) => {
      return new PlannedBreak({
        id: 0,
        startAt: formatDate(form.get('start_at').value),
        endAt: formatDate(form.get('end_at').value),
        defaultShiftId: form.get('id').value,
        duration: +form.get('duration').value,
        breakType: form.get('break_type').value,
        breakConfigurationId: +form.get('break_configuration.id').value,
        breakConfigurationName: form.get('break_configuration.name').value,
        breakConfigurationPaid: form.get('break_configuration.paid').value,
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannedBreakForms, plannedBreakFormsStr])

  useEffect(() => {
    dispatch({ type: 'RESET_DAYS_BY_ROW', selectedDaysByRow: daysByRow })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysByRowStr])

  useUnsafeEffect(() => {
    // Preview is not supported in the work areas view
    if (view.isByAreas) return

    // Adding timeout to avoid multiple dispatches when the user is typing or updating.
    clearTimeout(resetShiftsTimeout.current)
    resetShiftsTimeout.current = setTimeout(() => {
      const shifts = isCustomShift ? buildCustomShift() : buildSavedShifts()
      dispatch({ type: 'RESET_SHIFTS', shifts })
    }, 500)
  }, [buildCustomShift, buildSavedShifts, isCustomShift, dispatch, view])

  useUnsafeEffect(() => {
    // Adding timeout to avoid multiple dispatches when the user is typing or updating.
    clearTimeout(resetPlannedBreaksTimeout.current)
    resetPlannedBreaksTimeout.current = setTimeout(() => {
      const plannedBreaks = buildPlannedBreaks()
      dispatch({ type: 'RESET_PLANNED_BREAKS', plannedBreaks })
    }, 500)
  }, [buildPlannedBreaks, dispatch])

  useEffect(() => {
    // Preview is not supported in the work areas view
    if (view.isByAreas) return

    dispatch({ type: 'HIDE_SHIFTS', shiftIdsToHide })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftIdsToHideStr])

  const cleanPreview = useUnsafeCallback(() => {
    dispatch({ type: 'CLEAN_PREVIEW' })
  }, [dispatch])

  return {
    cleanPreview,
  }
}

export { useAddShiftPreview }
